import { mdiChevronDown, mdiChevronUp } from '@mdi/js';
import { useQueryClient } from '@tanstack/react-query';
import { Dropdown, message } from 'antd';
import type { CustomActionEntity } from 'api/models/mainservice';
import Button from 'components/Button/Button';
import ErrorMessage from 'components/ErrorAlert/ErrorMessage';
import Icon from 'components/Icon/Icon';
import { useCustomButtons } from 'helpers/customActionButtonHelpers';
import { useCustomTranslation } from 'helpers/translationHelper';
import type { CustomButtonSupportedIcon } from 'pages/settings/companySettings/components/customButtons/helpers';
import { getCustomButtonSupportedIconByName } from 'pages/settings/companySettings/components/customButtons/helpers';
import {
  useExecuteCustomActionButtonMutation,
  useExecuteCustomActionButtonOnEntityMutation
} from 'pages/settings/companySettings/components/customButtons/queries';
import { useCallback, useState } from 'react';

import CustomButton from './CustomButton';

interface UserCustomButtonsProps {
  entity: CustomActionEntity;
  entityId?: string; // If passed we know we are on the entity detail and isDetail filter should be used
}

const UserCustomButtons = ({ entity, entityId }: UserCustomButtonsProps) => {
  const { tSetting } = useCustomTranslation();

  const queryClient = useQueryClient();

  const customButtons = useCustomButtons(entity, !!entityId);

  const executeCustomActionButtonMutation = useExecuteCustomActionButtonMutation();
  const executeCustomActionButtonOnEntityMutation = useExecuteCustomActionButtonOnEntityMutation();

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const handleClick = useCallback(
    async (buttonId: string, forceRefresh?: boolean) => {
      let result = null;

      if (entityId) {
        result = await executeCustomActionButtonOnEntityMutation.mutateAsync({
          id: buttonId,
          entityId: entityId
        });
      } else {
        result = await executeCustomActionButtonMutation.mutateAsync(buttonId);
      }

      if (!result.isSuccess) {
        message.error(
          <ErrorMessage
            error={{
              errorMessage: result.detail,
              title: result.message || tSetting('custom_button_action_failed'),
              status: 400
            }}
          />,
          5
        );
      } else {
        if (forceRefresh) {
          await queryClient.invalidateQueries();
        }
        message.success(result.message ?? tSetting('custom_button_action_success'));
      }
    },
    [
      entityId,
      executeCustomActionButtonMutation,
      executeCustomActionButtonOnEntityMutation,
      tSetting,
      queryClient
    ]
  );

  if (customButtons.length === 0) return null;

  if (customButtons.length === 1) {
    return customButtons.map((button) => (
      <CustomButton
        key={button.id}
        button={button}
        onClick={() => handleClick(button.id, button.forceRefresh)}
      />
    ));
  }

  return (
    <Dropdown
      classNames={{ root: 'UserCustomButtons' }}
      menu={{
        items: customButtons.map((button) => ({
          key: button.id,
          label: button.label,
          icon: button.icon && (
            <Icon
              path={getCustomButtonSupportedIconByName(button.icon as CustomButtonSupportedIcon)}
            />
          ),
          onClick: () => handleClick(button.id, button.forceRefresh)
        }))
      }}
      open={isDropdownOpen}
      onOpenChange={(open) => setIsDropdownOpen(open)}
    >
      <Button>
        {tSetting('custom_buttons_dropdown')}
        <Icon path={isDropdownOpen ? mdiChevronUp : mdiChevronDown} />
      </Button>
    </Dropdown>
  );
};

export default UserCustomButtons;
